/**
 * The transient half of a chat: the turn currently in flight.
 *
 * Owns the subscription, the state machine, the streamed-text buffer, and the two
 * things that stop a dropped event from wedging the chat forever — a reconnect
 * hook and a stall watchdog.
 *
 * Nothing here is written to the Apollo cache. Token deltas arrive dozens of times
 * a second; normalizing each one would be absurd, and the durable copy of the
 * answer is written by the server anyway.
 */
import { useSubscription } from "@apollo/client/react";
import * as Sentry from "@sentry/react-native";
import { AgentSessionEventsDocument } from "@repo/graphql/documents/agent/subscription.generated";
import { wsGeneration } from "@repo/graphql/client/states";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { retireAfterRead } from "../../shared/agent/reconcile";
import { isTurnActive } from "../../shared/agent/turnReducer";
import {
  AgentTurnStore,
  resumeIdFor,
  type ResumePoint,
} from "../../shared/agent/turnStore";
import { toAgentEvent } from "../../shared/agent/types";

/**
 * How long a turn may go without a single event before we stop believing in it.
 *
 * An event can still be missed on a healthy socket (a reconnect, a stream that
 * expired), and a worker can die mid-run — in which case the server's own `isRunning`
 * stays true until the reaper notices, about a minute later or a few more if the
 * run's queue entry was lost. Rather than leave the user staring at a locked
 * composer, we reconcile against the database and let them try again. Unlocking too
 * early costs a toast; locking too long bricks the chat.
 */
const STALL_TIMEOUT_MS = 90_000;
const STALL_POLL_MS = 15_000;

const useAgentTurn = (
  sessionId: string | undefined,
  reconcile: () => Promise<void>,
) => {
  // One store per session. Keyed on sessionId so switching chats cannot leak a
  // half-streamed answer from one thread into another.
  const store = useMemo(() => new AgentTurnStore(), [sessionId]);
  useEffect(() => () => store.destroy(), [store]);

  const turn = useSyncExternalStore(store.subscribeState, store.getState);

  // Refs, not deps: the subscription's onData closure is created once, and we do
  // not want a new subscription every time `reconcile` gets a new identity.
  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;

  // Where to resume from after a reconnect. Absent until the socket has dropped once:
  // a first open has nothing to resume, and the server answers it with a snapshot.
  // Changing it changes the subscription's variables, which is what makes Apollo
  // open a new subscription that replays everything after this id. Keyed by store so
  // a point taken in one chat is never applied to the next one.
  const [resume, setResume] = useState<ResumePoint | null>(null);
  const resumeFrom = resumeIdFor(resume, store);

  useSubscription(AgentSessionEventsDocument, {
    variables: {
      sessionId: sessionId ?? "",
      afterEventId: resumeFrom ?? undefined,
    },
    skip: !sessionId,
    // `data` on useSubscription is last-value-wins: under React batching two
    // deltas in the same tick would overwrite each other and tokens would go
    // missing, silently. onData sees every event. ignoreResults also stops Apollo
    // re-rendering this hook on every single token.
    ignoreResults: true,
    onData: ({ data }) => {
      const raw = data.data?.agentSessionEvents;
      if (!raw) return;
      const event = toAgentEvent(raw);
      if (!event) return;

      // The store drops events it has already applied (graphql-ws resubscribes on
      // its own after a reconnect, and a resume replays), so a repeat comes back as
      // the same state and must not trigger a second reconcile.
      const before = store.getState();
      const next = store.dispatch({ kind: "EVENT", event, at: Date.now() });
      if (next === before) return;
      if (next.phase === "settling" || next.phase === "failed") {
        // The answer (or the failure) is final. Go and read the rows the server
        // just wrote, then retire the streaming overlay — whether or not the read
        // succeeded: it is a refresh, and a failed one must not strand the turn.
        void retireAfterRead(() => reconcileRef.current(), store, next.runId);
      }
    },
    onError: (error) => {
      // A subscription that never establishes looks exactly like a slow model:
      // the run is queued, nothing claims it, and the user waits out the stall
      // timeout. Surfacing it is the only way to tell the two apart.
      Sentry.captureException(error, {
        tags: { feature: "agent-chat" },
        extra: { sessionId },
      });
    },
  });

  // The socket came back. graphql-ws re-sends the subscription on its own, but with
  // the variables it was opened with — no `afterEventId` — so the server answers with
  // a snapshot, not with what was missed. Resubscribing from the last event we applied
  // fills that gap, and the store's id check makes the two overlapping subscriptions
  // harmless. The re-read stays as the fallback for whatever the replay cannot restore
  // (the stream keeps events for a bounded time, and a reload has no id at all).
  //
  // The resume point is taken inside the change callback, not in an effect after the
  // render it causes: that callback runs while the socket is still acknowledging, before
  // the resubscription has delivered a thing, so nothing from the new connection can
  // have moved the cursor yet.
  useEffect(() => {
    let stopWatching = () => {};
    const watch = () => {
      stopWatching = wsGeneration.onNextChange(() => {
        setResume({ store, id: store.getLastEventId() });
        if (isTurnActive(store.getState().phase)) {
          store.dispatch({ kind: "RECONNECTED", at: Date.now() });
        }
        // Always, not only mid-turn: a concept card or a saved answer that landed
        // while the socket was down is a row in the cache's blind spot, turn or no
        // turn.
        void reconcileRef.current();
        watch();
      });
    };
    watch();
    return () => stopWatching();
  }, [store]);

  // The backstop for everything the two paths above cannot see: a dropped event on
  // a live socket, or a worker that died without ever publishing a failure.
  //
  // `stalled` has to be real state rather than a `Date.now()` comparison at render
  // time — nothing would re-render the component at the moment it crossed the
  // threshold, so the escape hatch would never actually appear.
  const [stalled, setStalled] = useState(false);
  const active = isTurnActive(turn.phase);

  useEffect(() => {
    if (!active) {
      setStalled(false);
      return;
    }
    const timer = setInterval(() => {
      const current = store.getState();
      if (!isTurnActive(current.phase)) return;
      if (Date.now() - current.lastEventAt < STALL_TIMEOUT_MS) return;
      setStalled(true);
      void reconcileRef.current();
    }, STALL_POLL_MS);
    return () => clearInterval(timer);
  }, [active, store]);

  return { turn, store, stalled };
};

export default useAgentTurn;
